import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateGuestInput,
  CreateHouseholdWithMembersInput,
  Guest,
  Household,
  HouseholdRsvpInput,
  UpdateGuestInput,
  UpdateHouseholdInput,
} from '@guabi/shared';
import { guestTemplateCsv } from '@guabi/shared';
import { api, apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api';

export const guestsQueryKey = (wedId: string) => ['guests', wedId] as const;
export const householdsQueryKey = (wedId: string) => ['households', wedId] as const;

export function useGuests(wedId: string) {
  return useQuery({
    queryKey: guestsQueryKey(wedId),
    queryFn: () => apiGet<{ items: Guest[] }>(`/weddings/${wedId}/guests`),
    select: (r) => r.items,
  });
}

export function useHouseholds(wedId: string) {
  return useQuery({
    queryKey: householdsQueryKey(wedId),
    queryFn: () => apiGet<{ items: Household[] }>(`/weddings/${wedId}/households`),
    select: (r) => r.items,
  });
}

/**
 * Anything that changes a guest or a household moves the stats strip and the Home
 * tab, so all of them are invalidated together rather than leaving a stale number
 * on screen.
 */
function useGuestMutation<TArgs, TResult = void>(
  fn: (args: TArgs) => Promise<TResult>,
  wedId: string,
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: guestsQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: householdsQueryKey(wedId) });
      void qc.invalidateQueries({ queryKey: ['summary', wedId] });
    },
  });
}

export function useCreateGuest(wedId: string) {
  return useGuestMutation<CreateGuestInput, Guest>(
    (input) => apiPost<Guest>(`/weddings/${wedId}/guests`, input),
    wedId,
  );
}

export function useUpdateGuest(wedId: string) {
  return useGuestMutation<
    { guestId: string; input: UpdateGuestInput & { updatedAt: string } },
    Guest
  >(({ guestId, input }) => apiPatch<Guest>(`/weddings/${wedId}/guests/${guestId}`, input), wedId);
}

export function useDeleteGuest(wedId: string) {
  return useGuestMutation<{ guestId: string }>(
    ({ guestId }) => apiDelete(`/weddings/${wedId}/guests/${guestId}`),
    wedId,
  );
}

export interface HouseholdWithMembers {
  household: Household;
  members: Guest[];
}

export function useCreateHousehold(wedId: string) {
  return useGuestMutation<CreateHouseholdWithMembersInput, HouseholdWithMembers>(
    (input) => apiPost<HouseholdWithMembers>(`/weddings/${wedId}/households`, input),
    wedId,
  );
}

export function useUpdateHousehold(wedId: string) {
  return useGuestMutation<
    { householdId: string; input: UpdateHouseholdInput & { updatedAt: string } },
    Household
  >(
    ({ householdId, input }) =>
      apiPatch<Household>(`/weddings/${wedId}/households/${householdId}`, input),
    wedId,
  );
}

export function useAddHouseholdMember(wedId: string) {
  return useGuestMutation<
    { householdId: string; input: Omit<CreateGuestInput, 'householdId'> },
    Guest
  >(
    ({ householdId, input }) =>
      apiPost<Guest>(`/weddings/${wedId}/households/${householdId}/members`, input),
    wedId,
  );
}

export function useSetHouseholdRsvp(wedId: string) {
  return useGuestMutation<{ householdId: string; input: HouseholdRsvpInput }, HouseholdWithMembers>(
    ({ householdId, input }) =>
      apiPost<HouseholdWithMembers>(`/weddings/${wedId}/households/${householdId}/rsvp`, input),
    wedId,
  );
}

export function useDeleteHousehold(wedId: string) {
  return useGuestMutation<{ householdId: string }>(
    ({ householdId }) => apiDelete(`/weddings/${wedId}/households/${householdId}`),
    wedId,
  );
}

/** A household proposed by a parser or the model, before anything is saved. */
export interface ProposedHousehold {
  label: string;
  address?: Record<string, string>;
  members: (Omit<CreateGuestInput, 'householdId'> & { lastName?: string })[];
}

export interface ImportProposal {
  households: ProposedHousehold[];
  errors: { line: number; message: string }[];
  ignoredColumns: string[];
  people: number;
  source: 'csv' | 'vcard' | 'photo';
  /** A whole address book arrives with nothing ticked; a chosen file with everything. */
  defaultSelection: 'all' | 'none';
  usage?: { tokensIn: number; tokensOut: number; model: string };
}

export interface AiUsageState {
  used: number;
  budget: number | null;
  remaining: number | null;
  calls: number;
  estimatePerPhoto: number;
}

export const aiUsageQueryKey = (wedId: string) => ['ai-usage', wedId] as const;

/** What is left of the month, so the import screen can say so before you start. */
export function useAiUsage(wedId: string, enabled = false) {
  return useQuery({
    queryKey: aiUsageQueryKey(wedId),
    queryFn: () => apiGet<AiUsageState>(`/weddings/${wedId}/ai/usage`),
    enabled,
  });
}

/** Reads a file into a proposal. Writes nothing, so it is a plain mutation. */
export function useParseImport(wedId: string) {
  return useMutation<ImportProposal, Error, { format: 'csv' | 'vcard'; text: string }>({
    mutationFn: (body) => apiPost<ImportProposal>(`/weddings/${wedId}/guests/import/parse`, body),
  });
}

/**
 * Reads one photo. Writes nothing but the usage counter, so it does not
 * invalidate the guest caches — three refetches per photo for no reason.
 */
export function useExtractFromPhoto(wedId: string) {
  const qc = useQueryClient();
  return useMutation<ImportProposal, Error, { image: { mediaType: string; data: string } }>({
    mutationFn: (body) =>
      api<ImportProposal>(`/weddings/${wedId}/ai/extract-guests`, {
        method: 'POST',
        body: JSON.stringify(body),
        // Inside the Lambda's 29s, so a slow read aborts here with something
        // specific rather than arriving as an unparseable gateway error.
        timeoutMs: 28_000,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: aiUsageQueryKey(wedId) });
    },
  });
}

/** Saves what the review kept. This one does write, so the caches are invalidated. */
export function useApplyImport(wedId: string) {
  return useGuestMutation<{ households: ProposedHousehold[] }, ImportReport>(
    (body) => apiPost<ImportReport>(`/weddings/${wedId}/guests/import/apply`, body),
    wedId,
  );
}

export interface ImportReport {
  created: number;
  households: number;
  errors: { line: number; message: string }[];
  ignoredColumns: string[];
}

/**
 * The export comes back as CSV text, not JSON, so it goes through `api` with the
 * raw flag and is handed to the browser as a download.
 */
export async function downloadGuestCsv(wedId: string, filename = 'guabi-guests.csv') {
  const csv = await api<string>(`/weddings/${wedId}/guests/export`, { raw: true });
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/**
 * Empties the guest list. The confirmation is the wedding's own name, checked
 * again on the server — a dialog can be clicked past, a name has to be read.
 */
export function useClearGuests(wedId: string) {
  return useGuestMutation<{ confirm: string }, { guests: number; households: number }>(
    (input) =>
      apiPost<{ guests: number; households: number }>(`/weddings/${wedId}/guests/clear`, input),
    wedId,
  );
}

/**
 * The fill-in template, built in the browser and handed straight to the user.
 *
 * No request: the column list lives in `@guabi/shared`, so the file the app
 * offers and the file it can read are generated from one definition. A template
 * fetched from an endpoint would also be one more thing to be signed in for,
 * when the point is to send it to whoever is keeping the guest list.
 */
export function downloadGuestTemplate(language: string) {
  const locale = language.startsWith('es') ? 'es' : 'en';
  const csv = guestTemplateCsv(locale);
  // `text/csv` with a BOM is what makes Excel open it as UTF-8 rather than
  // mangling every accented heading we just wrote.
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = locale === 'es' ? 'guabi-invitados-plantilla.csv' : 'guabi-guest-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}
